/**
 * @fileoverview Local-first NERIS boundary service for system-web.
 * @module neris/boundaryService
 *
 * Responsibilities:
 * - Search fire departments by name via the live credentialed NERIS API
 * - Fetch jurisdiction boundary polygons from the NERIS pygeoapi endpoint
 * - Cache all results in Dexie fire_boundaries table (30-day TTL)
 * - Provide local-only lookup/search fallbacks for existing fireBoundaries data
 *
 * Dependencies:
 * - @firesquirrel/local-db — Dexie system database (fire_boundaries table)
 * - apiClient (api.onboarding.*) — HTTP calls to system-api routes
 *
 * Architecture:
 * - All reads check Dexie first; API is called only on cache miss or stale data
 * - Live search (searchByNameLive) calls /api/neris/entity-search — requires
 *   NERIS OAuth2 credentials on the backend; throws NERIS_NOT_CONFIGURED if absent
 * - Polygon fetch (fetchPolygonByNerisId) calls /api/neris/polygon/:nerisId;
 *   stores null geometry if the department has no pygeoapi polygon
 * - fireBoundaries (~35K records) is reference data — we do NOT bulk-sync;
 *   records are cached on-demand
 *
 * @localFirst
 * Source of Truth:
 * - local (Dexie fire_boundaries table), refreshed from upstream on cache miss
 * Write Flow:
 * - All API results are written to Dexie immediately after receipt
 * Sync Strategy:
 * - On-demand pull; 30-day TTL; no server push
 * Conflict Resolution:
 * - Last-write wins (reference data; no user edits possible)
 *
 * @storage fire_boundaries
 * Type:
 * - IndexedDB (Dexie)
 * Schema:
 * - _id: nerisId (PK), nerisId, neris.{name, departmentType},
 *   jurisdiction.state.{name, code, abbr}, geometry (GeoJSON | null),
 *   cachedAt (entity metadata freshness), geometryCachedAt (polygon
 *   freshness — only advanced by writes that actually carried a polygon
 *   result), updatedAt
 * Sync Role:
 * - Read-only cache; written by this service; never pushed to server
 *
 * @errorHandling
 * Strategy:
 * - NERIS_NOT_CONFIGURED (503) is re-thrown as a typed error for the UI to surface
 * - Polygon absence (geometry: null) is NOT an error — returned as-is
 * - Dexie failures are caught and are non-fatal; API result is still returned
 * User Impact:
 * - Hard block shown in modal when NERIS API credentials are absent
 * - Non-blocking warning shown when polygon is unavailable
 * Logging:
 * - REQUIRED — console.warn for Dexie failures, console.error for API failures
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { api } from '../apiClient';

const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isFresh(cachedAt: string | undefined): boolean {
  if (!cachedAt) return false;
  return Date.now() - new Date(cachedAt).getTime() < CACHE_TTL_MS;
}

/**
 * Polygon freshness is tracked separately from entity-metadata freshness so
 * that name/state refreshes (which carry no polygon) never extend the TTL of
 * a previously downloaded boundary. Records written before `geometryCachedAt`
 * existed fall back to `cachedAt`.
 */
function geometryFreshnessOf(doc: any): string | undefined {
  return doc?.geometryCachedAt ?? doc?.cachedAt;
}

// ---------------------------------------------------------------------------

/**
 * @contract lookupByNerisId
 *
 * Purpose:
 * - Verify a NERIS ID and return its boundary data from Dexie cache or local DB
 *
 * Fields:
 * - nerisId: NERIS canonical ID (uppercased)
 *
 * Rules:
 * - Returns null if NERIS ID is not found in fireBoundaries (not an error)
 * - Auth errors (401/403) are re-thrown; caller must handle
 *
 * Sync:
 * - Dexie-first, 30-day TTL; writes API result back to cache on miss
 */
export async function lookupByNerisId(nerisId: string): Promise<{
  nerisId: string;
  departmentName?: string;
  state?: string;
  boundary: any;
} | null> {
  const id = nerisId.trim().toUpperCase();

  // 1. Try Dexie cache first
  try {
    const db = await getSystemDb();
    const cached = await db.fire_boundaries.get(id);
    if (cached) {
      const doc = cached as any;
      if (isFresh(doc.cachedAt)) {
        return {
          nerisId: doc.nerisId ?? id,
          departmentName: doc.neris?.name,
          state: doc.jurisdiction?.state?.name,
          boundary: doc.geometry ?? null,
        };
      }
    }
  } catch {
    // Dexie unavailable — fall through to API
  }

  // 2. Cache miss: call API
  try {
    const result = await api.onboarding.nerisLookup(id);
    if (!result) return null;

    // Write result back to Dexie for future offline lookups.
    // Never downgrade an already-cached polygon to null — this endpoint reads
    // local fireBoundaries, which may legitimately have no geometry for a
    // department whose polygon was fetched from NERIS pygeoapi separately.
    try {
      const db = await getSystemDb();
      const now = new Date().toISOString();
      const existing = await db.fire_boundaries.get(id) as any;
      await db.fire_boundaries.put({
        ...(existing ?? {}),
        _id: id,
        nerisId: result.nerisId ?? id,
        neris: { ...(existing?.neris ?? {}), name: result.departmentName },
        jurisdiction: { ...(existing?.jurisdiction ?? {}), state: { name: result.state } },
        geometry: result.boundary ?? existing?.geometry ?? null,
        geometryCachedAt: result.boundary ? now : geometryFreshnessOf(existing),
        cachedAt: now,
        updatedAt: now,
      });
    } catch {
      // Non-fatal: Dexie write failed, API result still returned
    }

    return result;
  } catch (err: any) {
    if (err?.message?.includes('401') || err?.message?.includes('403') || err?.message?.includes('Authentication')) throw err;
    return null;
  }
}

/**
 * @contract searchByName
 *
 * Purpose:
 * - Search local fireBoundaries by name (Dexie cache → local MongoDB fallback)
 * - Used by BoundarySearchPanel and as a local fallback when live API is absent
 *
 * Rules:
 * - Returns up to 25 matches
 * - Serves from Dexie only when ≥25 fresh records match (full page assumption)
 * - Auth errors (401/403) are re-thrown
 *
 * Sync:
 * - Caches each result from the API individually in Dexie (30-day TTL)
 */
export async function searchByName(q: string, state?: string): Promise<Array<{
  nerisId: string;
  departmentName?: string;
  departmentType?: string;
  state?: string;
  stateCode?: string;
  boundary: any;
}>> {
  const query = q.trim();
  if (!query) return [];

  // 1. Try Dexie first — simple name-contains search on cached records
  try {
    const db = await getSystemDb();
    const docs = await db.fire_boundaries.toArray() as any[];
    const lq = query.toLowerCase();
    const stateFilter = state?.toUpperCase();

    const localMatches = docs.filter((doc: any) => {
      const name: string = (doc.neris?.name ?? '').toLowerCase();
      if (!name.includes(lq)) return false;
      if (stateFilter && doc.jurisdiction?.state?.abbr?.toUpperCase() !== stateFilter) return false;
      return isFresh(doc.cachedAt);
    });

    // Only serve from cache when the local set is as large as a full API page
    // (25 results). Fewer than that means the API likely has more matches we
    // haven't cached yet — always fall through so the user sees the full set.
    if (localMatches.length >= 25) {
      return localMatches.slice(0, 25).map((doc: any) => ({
        nerisId: doc.nerisId ?? doc._id,
        departmentName: doc.neris?.name,
        departmentType: doc.neris?.departmentType,
        state: doc.jurisdiction?.state?.name,
        stateCode: doc.jurisdiction?.state?.code,
        boundary: doc.geometry ?? null,
      }));
    }
  } catch {
    // Dexie unavailable — fall through to API
  }

  // 2. Cache miss (or insufficient local results): call API
  try {
    const result = await api.onboarding.nerisSearch(query, state);
    const results = result.results ?? [];

    // Cache results individually in Dexie
    if (results.length > 0) {
      try {
        const db = await getSystemDb();
        const now = new Date().toISOString();
        for (const r of results) {
          if (!r.nerisId) continue;
          // Preserve an existing polygon when this result carries none.
          const existing = await db.fire_boundaries.get(r.nerisId) as any;
          await db.fire_boundaries.put({
            ...(existing ?? {}),
            _id: r.nerisId,
            nerisId: r.nerisId,
            neris: { ...(existing?.neris ?? {}), name: r.departmentName, departmentType: r.departmentType },
            jurisdiction: {
              ...(existing?.jurisdiction ?? {}),
              state: { name: r.state, code: r.stateCode, abbr: state?.toUpperCase() },
            },
            geometry: r.boundary ?? existing?.geometry ?? null,
            geometryCachedAt: r.boundary ? now : geometryFreshnessOf(existing),
            cachedAt: now,
            updatedAt: now,
          });
        }
      } catch {
        // Non-fatal: Dexie write failed, API results still returned
      }
    }

    return results;
  } catch (err: any) {
    if (err?.message?.includes('401') || err?.message?.includes('403') || err?.message?.includes('Authentication')) throw err;
    return [];
  }
}

// ---------------------------------------------------------------------------
// Live-API functions (require backend NERIS credentials)
// ---------------------------------------------------------------------------

/**
 * @contract NerisLiveSearchResult
 *
 * Purpose:
 * - Shape of a single department entity returned by the live NERIS API search
 *
 * Fields:
 * - nerisId: NERIS canonical ID (e.g. "FD04013001")
 * - name: official department name
 * - city: city of record
 * - state: 2-letter state abbreviation
 * - zipCode: ZIP of record
 * - address: primary address line
 * - fdid: NFIRS Fire Department ID
 *
 * Sync:
 * - Written to Dexie fire_boundaries after searchByNameLive returns
 */
export interface NerisLiveSearchResult {
  nerisId: string;
  name: string;
  city?: string;
  state?: string;
  zipCode?: string;
  address?: string;
  fdid?: string;
  raw?: Record<string, any>;
}

function getPrimaryBoundaryUrl(result: NerisLiveSearchResult): string | undefined {
  const regionSets = Array.isArray(result.raw?.region_sets) ? result.raw.region_sets : [];
  for (const regionSet of regionSets) {
    const regions = Array.isArray(regionSet?.regions) ? regionSet.regions : [];
    const boundary = regions.find((region: any) => typeof region?.url === 'string' && region.url.includes('/pygeoapi/collections/'));
    if (boundary?.url) return boundary.url;
  }
  return undefined;
}

export function getBoundaryUrlFromNerisResult(result: NerisLiveSearchResult): string | undefined {
  return getPrimaryBoundaryUrl(result);
}

/**
 * @contract searchByNameLive
 *
 * Purpose:
 * - Search fire departments via the live credentialed NERIS API.
 *   Called by DepartmentInfoStep when the user opens the "Look up" modal.
 *
 * Rules:
 * - Throws { code: 'NERIS_NOT_CONFIGURED' } when backend credentials are absent (503)
 * - Re-throws this error so the UI can show the hard-block state
 * - Results are cached individually in Dexie (30-day TTL)
 *
 * Sync:
 * - Online-only for the search; Dexie write is best-effort (non-fatal on failure)
 */
export async function searchByNameLive(
  q: string,
  state?: string,
): Promise<NerisLiveSearchResult[]> {
  const query = q.trim();
  if (!query) return [];

  try {
    const result = await api.onboarding.nerisEntitySearch(query, state) as {
      results?: NerisLiveSearchResult[];
    };
    const results = result.results ?? [];

    if (results.length > 0) {
      try {
        const db = await getSystemDb();
        const now = new Date().toISOString();
        for (const r of results) {
          if (!r.nerisId) continue;
          // Preserve any geometry already cached for this department. A search
          // result carries no polygon, so writing `geometry: null` here used to
          // DESTROY a boundary that had been downloaded earlier — the next
          // `fetchPolygonByNerisId` then reported "no polygon available" for a
          // department whose boundary had in fact been retrieved successfully.
          // Entity metadata is refreshed; geometry is left untouched.
          const existing = await db.fire_boundaries.get(r.nerisId) as any;
          await db.fire_boundaries.put({
            ...(existing ?? {}),
            _id: r.nerisId,
            nerisId: r.nerisId,
            neris: { ...(existing?.neris ?? {}), name: r.name },
            jurisdiction: {
              ...(existing?.jurisdiction ?? {}),
              state: { name: r.state, abbr: r.state?.toUpperCase() },
            },
            geometry: existing?.geometry ?? null,
            geometryCachedAt: geometryFreshnessOf(existing),
            cachedAt: now,
            updatedAt: now,
          });
        }
      } catch (dexieErr) {
        console.warn('[nerisBoundaryService] Dexie write failed after live search:', dexieErr);
      }
    }

    return results;
  } catch (err: any) {
    if (err?.status === 503 || err?.message?.includes('NERIS_NOT_CONFIGURED') || err?.code === 'NERIS_NOT_CONFIGURED') {
      const configured: { code: string; message: string } = {
        code: 'NERIS_NOT_CONFIGURED',
        message: 'NERIS API is not configured. Contact your system administrator.',
      };
      throw configured;
    }
    console.error('[nerisBoundaryService] Live search failed:', err.message);
    throw err;
  }
}

/**
 * @contract lookupEntityByNerisId
 *
 * Purpose:
 * - Resolve a single fire department entity from the live NERIS API by its
 *   exact NERIS ID. Used by the Verify button flow in DepartmentInfoStep.
 *
 * Rules:
 * - Passes the NERIS ID as the search query; returns the exact-match result
 *   first, falling back to the first result if no exact match is found.
 * - Returns null when the API returns no results for the given ID.
 * - Propagates NERIS_NOT_CONFIGURED so the caller can show the hard block.
 *
 * Sync:
 * - Delegates to searchByNameLive; results are cached in Dexie by that call.
 */
export async function lookupEntityByNerisId(
  nerisId: string,
): Promise<NerisLiveSearchResult | null> {
  const id = nerisId.trim();
  if (!id) return null;
  const results = await searchByNameLive(id); // propagates NERIS_NOT_CONFIGURED
  return (
    results.find((r) => r.nerisId.toUpperCase() === id.toUpperCase()) ??
    results[0] ??
    null
  );
}

/**
 * @contract fetchPolygonByNerisId
 *
 * Purpose:
 * - Fetch the jurisdiction boundary polygon for a verified NERIS ID from
 *   the NERIS pygeoapi endpoint. Called automatically after a user selects
 *   a department in the onboarding wizard lookup modal.
 *
 * Rules:
 * - Checks Dexie first — returns cached geometry if fresh AND not null
 * - Stores null geometry in Dexie to prevent re-fetching a known gap
 * - Returns null (no throw) when the department has no polygon
 * - Network/server errors propagate so the UI can show a non-blocking warning
 *
 * Sync:
 * - Polygon result (including null) is cached in Dexie; TTL is 30 days
 */
export async function fetchPolygonByNerisId(
  nerisId: string,
  regionUrl?: string,
): Promise<import('geojson').Polygon | import('geojson').MultiPolygon | null> {
  const id = nerisId.trim().toUpperCase();

  // 1. Check Dexie cache — return if fresh AND the cached record has geometry
  try {
    const db = await getSystemDb();
    const cached = await db.fire_boundaries.get(id) as any;
    if (cached && isFresh(geometryFreshnessOf(cached)) && cached.geometry !== undefined) {
      // If a regionUrl is provided and the cached result is null, fall through to
      // the API — the pygeoapi endpoint may now have geometry that wasn't available
      // when this record was last fetched.
      if (cached.geometry !== null || !regionUrl) {
        return cached.geometry ?? null;
      }
    }
  } catch {
    // Dexie unavailable — fall through to API
  }

  // 2. Fetch from backend (NERIS pygeoapi via regionUrl, or null if absent)
  try {
    const result = await api.onboarding.nerisPolygon(id, regionUrl) as {
      nerisId: string;
      geometry: import('geojson').Polygon | import('geojson').MultiPolygon | null;
    };

    const geometry = result.geometry ?? null;

    // Write back to Dexie (even null is stored to mark "checked, not available")
    try {
      const db = await getSystemDb();
      const now = new Date().toISOString();
      const existing = await db.fire_boundaries.get(id) as any;
      await db.fire_boundaries.put({
        ...(existing ?? {}),
        _id: id,
        nerisId: id,
        geometry,
        geometryCachedAt: now,
        cachedAt: now,
        updatedAt: now,
      });
    } catch (dexieErr) {
      console.warn('[nerisBoundaryService] Dexie write failed after polygon fetch:', dexieErr);
    }

    return geometry;
  } catch (err: any) {
    console.error('[nerisBoundaryService] Polygon fetch failed for', id, ':', err.message);
    throw err;
  }
}
